import React, { useMemo, useCallback } from "react";
import ReactECharts from "src/utils/EChartsReact";
import { formatNumber, convertStringToNumeric } from "src/pages/Insights/transformer/dashboardUtils";
import { getCommonTooltipConfig, buildChartTooltip, useTooltipCoordination } from "../tooltipFormatter";
import { hasAnyStatMarkers, resolveStatMarkerRows, buildHClusteredMarkPoints } from "./../../components/StatMarkerUtils";
import { chartConfig } from "../../chartConfig";
import { IStatMarker, IStatTestConfig } from "src/pages/Insights/models/interfaces";
import { useScaledConfig } from "../../ChartScaleContext";
import { getNSizeStatusLabel, NSizeLabelText, SUPPRESSED_VALUE_PLACEHOLDER, SUPPRESSED_VALUE_COLOR } from "../chartHelpers";

interface IMiniBarChartProps {
	series: {
		name: string;
		value: number;
		n_size: number | string;
		stat_marker?: IStatMarker | null;
	}[];
	waves: Record<string, string>;
	maxN: number;
	labelFormat: string;
	containerHeight: number;
	containerWidth?: number;
	xAxisDomain: { min: number; max: number; interval: number };
	totalBarsInRow: number;
	statTestConfig?: IStatTestConfig | null;
}

const MiniBarChart: React.FC<IMiniBarChartProps> = ({ maxN, series, waves, labelFormat, containerHeight, containerWidth, xAxisDomain, totalBarsInRow, statTestConfig }) => {
	const scaled = useScaledConfig();
	const config = useMemo(() => {
		const dimensionsConfig = chartConfig.getDimensionsConfig();
		const typographyConfig = chartConfig.getTypographyConfig();
		const layoutConfig = chartConfig.getLayoutConfig();
		const hClusteredConfig = chartConfig.getHorizontalClusteredConfig();

		return {
			// 🔥 scale bar thickness limits
			barMinHeight: Math.max(4, dimensionsConfig.bar.minHeight * scaled.scaleFactor),

			barMaxHeight: Math.min(dimensionsConfig.bar.maxHeight, scaled.geometry.maxBarWidth),

			// 🔥 scaled label font
			labelFontSize: scaled.typography.dataLabel,

			// 🔥 scaled grid paddings
			// gridLeft mirrors gridRight when domain has negative values so bars
			// align with the XAxisScale's negative-side padding.
			gridLeft: xAxisDomain.min < 0 ? layoutConfig.grid.right : 0,
			gridRight: layoutConfig.grid.right,
			gridTop: scaled.geometry.gridTop,
			// Mirror gridTop so plot area is vertically symmetric within the
			// canvas — keeps bars centered against the y-axis label cell.
			// (ScaledGeometry has no gridBottom field; reading it returns
			// undefined and ECharts defaults to 60, pushing bars upward.)
			gridBottom: scaled.geometry.gridTop,

			// 🔥 scaled marker layout
			statMarkerLayout: {
				...hClusteredConfig.statMarkerLayout,
				gridPadding: hClusteredConfig.statMarkerLayout.gridPadding * scaled.scaleFactor,
			},
		};
	}, [scaled, xAxisDomain.min]);

	const { chartRef, onEvents } = useTooltipCoordination();

	// Check if any series has stat markers (new structure)
	const hasAnyMarkers = useMemo(() => hasAnyStatMarkers(series, statTestConfig), [series, statTestConfig]);

	// Tooltip formatter with new stat marker support
	const tooltipFormatter = useCallback((params: any) => buildChartTooltip(params, series, labelFormat, statTestConfig, waves), [series, labelFormat, statTestConfig, waves]);

	const GRID_VERTICAL_PADDING_PERCENT = 0.05;

	const barHeight = useMemo(() => {
		if (totalBarsInRow === 0) return config.barMinHeight;

		const availableHeight = containerHeight * (1 - GRID_VERTICAL_PADDING_PERCENT * 2);
		const idealBarHeight = Math.floor(availableHeight / totalBarsInRow);

		return Math.min(config.barMaxHeight, Math.max(config.barMinHeight, idealBarHeight));
	}, [containerHeight, totalBarsInRow, config]);

	// Processed series with new stat marker support
	const processedSeries = useMemo(() => {
		const gridLeft = config.gridLeft;
		const gridRight = hasAnyMarkers ? config.gridRight + config.statMarkerLayout.gridPadding : config.gridRight;
		const usableChartWidth = containerWidth ? containerWidth - gridLeft - gridRight : 0;
		const valueRange = xAxisDomain.max - xAxisDomain.min;

		return series.map((s) => {
			const isNoDataValue =
				s.value === undefined ||
				s.value === null ||
				(s.value as unknown) === "No Data" ||
				(s.value as unknown) === "No data" ||
				(typeof s.value === "number" && !Number.isFinite(s.value));

			const nSizeStatusLabel = getNSizeStatusLabel(s.n_size);
			const isNoDataNSize = nSizeStatusLabel === NSizeLabelText.NoData;
			const suppressValue = nSizeStatusLabel !== null;

			const formattedVal = isNoDataValue || suppressValue ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : formatNumber(s.value, labelFormat);
			const numeric = isNoDataValue || suppressValue ? 0 : convertStringToNumeric(formattedVal);
			const value = Number.isFinite(numeric) ? numeric : 0;

			const isSuppressed = isNoDataValue || suppressValue;
			const displayLabel = isSuppressed
				? (nSizeStatusLabel === NSizeLabelText.LowN ? NSizeLabelText.LowN : SUPPRESSED_VALUE_PLACEHOLDER)
				: value === 0 ? (labelFormat.endsWith("%") ? "0%" : "0") : formattedVal;

			// Resolve stat markers per CI row
			const resolvedRows = !isNoDataValue && !suppressValue && statTestConfig ? resolveStatMarkerRows(s.stat_marker, statTestConfig) : [];
			const hasMarkers = resolvedRows.length > 0;

			// NEW — account for wider '%' glyph and add format-aware padding
			const labelText = String(displayLabel);
			const isPercentFormat = labelFormat.endsWith("%");
			const charWidthMultiplier = config.labelFontSize * 0.65;
			const estimatedLabelWidth = labelText.length * charWidthMultiplier;
			const formatPadding = isPercentFormat ? charWidthMultiplier * 0.6 : 0; // '%' renders ~60% wider than avg digit
			const dataLabelWidth = Math.ceil(estimatedLabelWidth + formatPadding) + 4;

			// NEW — subtract dataLabelWidth from available space so marker doesn't overlap
			let availableForMarker = 0;
			if (containerWidth && usableChartWidth > 0 && valueRange > 0) {
				const barPixelWidth = (value / valueRange) * usableChartWidth;
				availableForMarker = Math.max(0, usableChartWidth - barPixelWidth - dataLabelWidth);
			}

			const markPointData = hasMarkers ? buildHClusteredMarkPoints(resolvedRows, statTestConfig!, dataLabelWidth, availableForMarker) : [];

			markPointData.forEach((pt: any) => {
				pt.dataPointMeta = {
					stat_marker: s.stat_marker ?? null,
					value: s.value,
					n_size: s.n_size,
					name: s.name,
				};
			});

			return {
				name: s.name,
				type: "bar" as const,
				data: [
					{
						value,
						n_size: isNoDataNSize ? NSizeLabelText.NoData : s.n_size,
						name: s.name,
						displayValue: isNoDataValue || suppressValue ? (nSizeStatusLabel ?? NSizeLabelText.NoData) : formattedVal,
						stat_marker: s.stat_marker ?? null,
					},
				],
				barWidth: barHeight,
				barGap: 0,
				barCategoryGap: 0,
				itemStyle: {
					color: waves[s.name],
					borderRadius: 0,
				},
				label: {
					show: true,
					fontWeight: "normal" as const,
					position: (Number(value) < 0 ? "left" : "right") as "left" | "right",
					fontSize: config.labelFontSize,
					formatter: () => displayLabel,
					...(isSuppressed ? { color: SUPPRESSED_VALUE_COLOR } : {}),
				},
				...(markPointData.length > 0
					? {
						markPoint: {
							symbol: "circle",
							symbolSize: 0,
							data: markPointData,
							animation: false,
							tooltip: { show: true },
						},
					}
					: {}),
			};
		});
	}, [series, waves, labelFormat, barHeight, config, statTestConfig, containerWidth, xAxisDomain, hasAnyMarkers]);

	const option = useMemo(
		() => ({
			animation: false,
			tooltip: {
				...getCommonTooltipConfig(),
				formatter: tooltipFormatter,
				renderMode: "html",
				className: "stat-marker-tooltip",
			},
			grid: {
				left: config.gridLeft,
				right: config.gridRight,
				top: config.gridTop,
				bottom: config.gridBottom,
				containLabel: true,
			},
			xAxis: {
				type: "value" as const,
				min: xAxisDomain.min,
				max: xAxisDomain.max,
				show: false,
			},
			yAxis: {
				type: "category" as const,
				data: [""],
				show: false,
			},
			series: processedSeries,
		}),
		[processedSeries, tooltipFormatter, xAxisDomain.min, xAxisDomain.max, config, hasAnyMarkers],
	);

	return <ReactECharts option={option} ref={chartRef} onEvents={onEvents} style={{ width: "100%", height: containerHeight + 20 }} autoResize opts={{ renderer: "svg" }} />;
};

export default React.memo(MiniBarChart, (prevProps, nextProps) => {
	return (
		prevProps.series === nextProps.series &&
		prevProps.waves === nextProps.waves &&
		prevProps.maxN === nextProps.maxN &&
		prevProps.labelFormat === nextProps.labelFormat &&
		prevProps.containerHeight === nextProps.containerHeight &&
		prevProps.containerWidth === nextProps.containerWidth &&
		prevProps.xAxisDomain.min === nextProps.xAxisDomain.min &&
		prevProps.xAxisDomain.max === nextProps.xAxisDomain.max &&
		prevProps.totalBarsInRow === nextProps.totalBarsInRow &&
		prevProps.statTestConfig === nextProps.statTestConfig
	);
});
